import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { API_URL } from '../config'
import logger from '../utils/logger'

const BusinessContext = createContext({})

export const useBusinessContext = () => {
  const context = useContext(BusinessContext)
  if (!context) {
    throw new Error('useBusinessContext must be used within BusinessContextProvider')
  }
  return context
}

export const BusinessContextProvider = ({ children }) => {
  const { user } = useAuth()
  // Initialize from localStorage if available (for advisors)
  const [currentBusinessId, setCurrentBusinessId] = useState(() => {
    const saved = localStorage.getItem('currentBusinessId')
    return saved ? parseInt(saved, 10) : null
  })
  const [currentBusiness, setCurrentBusiness] = useState(null)
  const [loading, setLoading] = useState(false)

  // For business owners, automatically use their business
  // For advisors, restore from localStorage if available ONLY on page refresh (not on login)
  // For viewers, automatically fetch their viewer access and set business context
  useEffect(() => {
    if (user && user.role === 'business_owner') {
      // Business owner - fetch their business
      fetchUserBusiness()
    } else if (user && user.role === 'viewer') {
      // Viewer - fetch their viewer access and set business context
      fetchViewerBusiness()
    } else if (user && user.role === 'advisor') {
      // Advisor - Only restore business context if we're NOT at the /advisor route
      // This prevents auto-loading a business when advisor first logs in
      const currentPath = window.location.pathname
      const isOnAdvisorRoute = currentPath === '/advisor'

      if (!isOnAdvisorRoute) {
        // Only restore from localStorage if not on advisor dashboard
        const savedBusinessId = localStorage.getItem('currentBusinessId')
        if (savedBusinessId) {
          const businessId = parseInt(savedBusinessId, 10)
          // If we have a saved business ID and no current business loaded, fetch it
          if (!isNaN(businessId) && !currentBusiness) {
            logger.debug('Restoring business context from localStorage', { businessId })
            selectBusiness(businessId)
          }
        }
      }
    }
  }, [user, currentBusiness])

  const fetchUserBusiness = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/business/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data && data.data.profile) {
          setCurrentBusinessId(data.data.profile.id)
          setCurrentBusiness(data.data.profile)
          localStorage.setItem('currentBusinessId', data.data.profile.id.toString())
        }
      }
    } catch (error) {
      logger.error('Error fetching business', error)
    }
  }

  const fetchViewerBusiness = async () => {
    try {
      const token = localStorage.getItem('token')
      logger.debug('Fetching viewer business context...')

      // First, get viewer's permissions which includes business_id
      const permResponse = await fetch(`${API_URL}/viewer/my-permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!permResponse.ok) {
        logger.error('Failed to fetch viewer permissions', { status: permResponse.status })
        return
      }

      const permData = await permResponse.json()
      logger.debug('Viewer permissions response', permData)

      if (permData.success && permData.business_id) {
        const businessId = permData.business_id
        logger.debug('Fetching business details', { businessId })

        // Fetch the business details
        const businessResponse = await fetch(`${API_URL}/advisor/business/${businessId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (businessResponse.ok) {
          const businessData = await businessResponse.json()
          if (businessData.success && businessData.business) {
            setCurrentBusinessId(businessId)
            setCurrentBusiness(businessData.business)
            localStorage.setItem('currentBusinessId', businessId.toString())
            logger.debug('Viewer business context set successfully', { businessId, businessName: businessData.business.business_name })
          } else {
            logger.error('Invalid business data response', businessData)
          }
        } else {
          logger.error('Failed to fetch business details', { status: businessResponse.status })
        }
      } else {
        logger.error('No business_id in viewer permissions response', permData)
      }
    } catch (error) {
      logger.error('Error fetching viewer business', error)
    }
  }

  const selectBusiness = async (businessId) => {
    if (!businessId) {
      setCurrentBusinessId(null)
      setCurrentBusiness(null)
      localStorage.removeItem('currentBusinessId')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/advisor/business/${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.business) {
          setCurrentBusinessId(businessId)
          setCurrentBusiness(data.business)
          localStorage.setItem('currentBusinessId', businessId.toString())
          logger.debug('Business selected in context', { businessId, businessName: data.business.business_name })
        }
      } else {
        logger.error('Failed to fetch business details', { businessId })
        setCurrentBusinessId(null)
        setCurrentBusiness(null)
        localStorage.removeItem('currentBusinessId')
      }
    } catch (error) {
      logger.error('Error selecting business', { error, businessId })
      setCurrentBusinessId(null)
      setCurrentBusiness(null)
      localStorage.removeItem('currentBusinessId')
    } finally {
      setLoading(false)
    }
  }

  const clearBusiness = () => {
    setCurrentBusinessId(null)
    setCurrentBusiness(null)
    localStorage.removeItem('currentBusinessId')
  }

  // Get headers with business context for API calls
  const getBusinessHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }

    if (currentBusinessId) {
      headers['X-Business-Id'] = currentBusinessId.toString()
    }

    return headers
  }

  const value = {
    currentBusinessId,
    currentBusiness,
    loading,
    selectBusiness,
    clearBusiness,
    getBusinessHeaders,
    isAdvisor: user?.role === 'advisor',
    isBusinessOwner: user?.role === 'business_owner',
    isViewer: user?.role === 'viewer'
  }

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  )
}
